import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButtonGroup, ToggleButton, Switch } from '@mui/material';
import { FaLanguage, FaGraduationCap } from 'react-icons/fa';
import axios from 'axios';
import { useAppContext } from '../../context/AppContext';
import { useNotification } from '../../components/notifications/useNotification';
import './Settings.css';

/**
 * Settings page.
 *
 * Lets the dietist manage application preferences:
 * - Interface language (IT / EN)
 * - Tutorial buttons visibility (toursEnabled)
 *
 * Preferences are persisted on the backend via
 * `PATCH /dietists/:id/preferences` and immediately reflected in
 * AppContext so the rest of the app reacts without a reload.
 *
 * @component
 * @returns {JSX.Element}
 */
const Settings = () => {
    const { t, i18n } = useTranslation();
    const { dietist, currentLanguage, setCurrentLanguage, toursEnabled, setToursEnabled } = useAppContext();
    const { showSuccess, showError } = useNotification();

    const [saving, setSaving] = useState(false);

    // Local copies — user edits these; context is only updated on Save
    const [localLang, setLocalLang] = useState(
        () => (currentLanguage || 'it').split('-')[0].toLowerCase()
    );
    const [localTours, setLocalTours] = useState(toursEnabled);

    // Sync local state when context is updated from the preference fetch
    useEffect(() => {
        setLocalLang((currentLanguage || 'it').split('-')[0].toLowerCase());
    }, [currentLanguage]);

    useEffect(() => {
        setLocalTours(toursEnabled);
    }, [toursEnabled]);

    const handleLanguageChange = (_, newLang) => {
        if (newLang) setLocalLang(newLang);
    };

    const handleSave = async () => {
        if (!dietist?._id) return;
        setSaving(true);
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/preferences`,
                { language: localLang, toursEnabled: localTours },
            );
            i18n.changeLanguage(localLang);
            setCurrentLanguage(localLang);
            setToursEnabled(localTours);
            showSuccess(t('settings-save-success'));
        } catch {
            showError(t('settings-save-error'));
        } finally {
            setSaving(false);
        }
    };

    const toggleSx = {
        '& .MuiToggleButton-root': {
            border: '1px solid #e2e8f0',
            color: '#64748b',
            fontWeight: 600,
            fontSize: '0.85rem',
            padding: '8px 20px',
            textTransform: 'none',
            letterSpacing: '0.02em',
            transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
            '&.Mui-selected': {
                backgroundColor: '#fc9f5b',
                color: '#ffffff',
                borderColor: '#fc9f5b',
                '&:hover': { backgroundColor: '#fd4701', borderColor: '#fd4701' },
            },
            '&:hover': { backgroundColor: '#f3f4f6' },
        },
    };

    const switchSx = {
        '& .MuiSwitch-switchBase.Mui-checked': { color: '#fd4701' },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#fc9f5b' },
    };

    return (
        <div className="sp">
            <div className="sp-card">

                {/* ── Language ─────────────────────────────────────── */}
                <div className="sp-section">
                    <div className="sp-section-header">
                        <FaLanguage className="sp-section-icon" />
                        <div>
                            <h2 className="sp-section-title">{t('settings-language-section')}</h2>
                            <p className="sp-section-desc">{t('settings-language-desc')}</p>
                        </div>
                    </div>
                    <ToggleButtonGroup
                        value={localLang}
                        exclusive
                        onChange={handleLanguageChange}
                        size="small"
                        aria-label={t('settings-language-section')}
                        sx={toggleSx}
                    >
                        <ToggleButton value="it" aria-label="Italian">IT — Italiano</ToggleButton>
                        <ToggleButton value="en" aria-label="English">EN — English</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <div className="sp-divider" />

                {/* ── Tutorials ────────────────────────────────────── */}
                <div className="sp-section">
                    <div className="sp-section-header">
                        <FaGraduationCap className="sp-section-icon" />
                        <div>
                            <h2 className="sp-section-title">{t('settings-tours-section')}</h2>
                            <p className="sp-section-desc">{t('settings-tours-desc')}</p>
                        </div>
                    </div>
                    <div className="sp-toggle-row">
                        <span className={`sp-toggle-label${localTours ? ' sp-toggle-label--on' : ''}`}>
                            {localTours ? t('settings-tours-enabled') : t('settings-tours-disabled')}
                        </span>
                        <Switch
                            checked={localTours}
                            onChange={(e) => setLocalTours(e.target.checked)}
                            inputProps={{ 'aria-label': t('settings-tours-section') }}
                            sx={switchSx}
                        />
                    </div>
                </div>

                <div className="sp-divider" />

                {/* ── Save ─────────────────────────────────────────── */}
                <div className="sp-footer">
                    <button
                        className="sp-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {t('Save')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default Settings;
